// src/pages/organization/matches/createMatch.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMainPageTitle } from "../../../contexts/PageContext";
import ProfileImage from '../../../components/profileImage';
const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";
const CreateMatch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  useEffect(() => {
    updatePageTitle(t("matches.createMatch"), t("matches.createNewMatch"));
  }, [updatePageTitle, t]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    team1: '',
    team2: '',
    startDate: '',
  });
  const token = localStorage.getItem('token');
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/team/get-all-my-organization-teams?page=1&limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      if (data && data.teams) {
        setTeams(data.teams);
      } else {
        setTeams([]);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      setError(t("errors.failedToLoad"));
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchTeams();
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [fetchTeams]);

  const handleChange = (name, value) => {
    console.log('Handling change:', name, value); // Debug log
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [name]: value,
      };
      if (name === 'team1' && value === prev.team2) {
        newFormData.team2 = '';
      }
      return newFormData;
    });
    setOpenDropdown(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/event/create-event-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        // Try to get the error message from the response
        try {
          const errorData = await response.json();
          
          // Handle subscription limit errors with specific messages
          if (errorData.error === "Monthly match limit exceeded") {
            const details = errorData.details;
            setError(`You have reached your monthly match limit (${details.maximumPerMonth} matches) for your ${details.package} subscription. Your limit will reset on ${details.period.split(' - ')[1]}.`);
          } else if (errorData.error === "Monthly event limit exceeded") {
            const details = errorData.details;
            setError(`You have reached your monthly event limit (${details.maximumPerMonth} events) for your ${details.package} subscription. Your limit will reset on ${details.period.split(' - ')[1]}.`);
          } else if (errorData.message) {
            setError(errorData.message);
          } else {
            setError(t("errors.failedToCreate"));
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          setError(t("errors.failedToCreate"));
        }
        return;
      }

      navigate('/organization/matches');
    } catch (error) {
      console.error('Error creating match:', error);
      setError(t("errors.failedToCreate"));
    } finally {
      setLoading(false);
    }
  };

  const getSelectedTeam = (teamId) => {
    return teams.find(team => team._id === teamId);
  };

  const TeamPreview = ({ teamId }) => {
    const team = getSelectedTeam(teamId);
    if (!team) return null;

    return (
      <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 flex-shrink-0">
            <ProfileImage
              image={team.logo}
              alt={team.name}
              className="w-full h-full object-cover rounded-full border-2 border-gray-200"
            />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>{t("teams.type")}: {team.type}</p>
              <p>{t("teams.sport")}: {team.sport}</p>
              <p>{t("teams.gender")}: {team.gender}</p>
              <p>{t("teams.members")}: {team.members?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CustomDropdown = ({ name, value, teams, label }) => {
    const selectedTeam = getSelectedTeam(value);
    const availableTeams = name === 'team2' 
      ? teams.filter(team => team._id !== formData.team1)
      : teams;

    return (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <div
          onClick={() => setOpenDropdown(openDropdown === name ? null : name)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white"
        >
          {selectedTeam ? (
            <div className="flex items-center space-x-3">
              <ProfileImage
                image={selectedTeam.logo}
                alt={selectedTeam.name}
                className="w-8 h-8 rounded-full object-cover"
              />
              <span>{selectedTeam.name}</span>
            </div>
          ) : (
            <span className="text-gray-500">{t("matches.selectTeam")}</span>
          )}
        </div>
        
        {openDropdown === name && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {availableTeams.length > 0 ? (
              availableTeams.map((team) => (
                <div
                  key={team._id}
                  onClick={() => {
                    handleChange(name, team._id);
                  }}
                  className="flex items-center space-x-3 px-4 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <ProfileImage
                    image={team.logo}
                    alt={team.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-medium">{team.name}</div>
                    <div className="text-sm text-gray-500">{team.type}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500 text-center">
                {t("matches.noTeamsAvailable")}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Filter teams for team2 selection
  // eslint-disable-next-line no-unused-vars
  const availableTeamsForTeam2 = teams.filter(team => team._id !== formData.team1);

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">{t("matches.createNewMatch")}</h1>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  {error.includes('monthly') ? 'Subscription Limit Reached' : 'Error Creating Match'}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                  {error.includes('monthly') && (
                    <div className="mt-3">
                      <button
                        onClick={() => navigate('/organization/subscription/packages')}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Upgrade Subscription
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              {t("matches.description")}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t("matches.descriptionPlaceholder")}
              required
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <CustomDropdown
              name="team1"
              value={formData.team1}
              teams={teams}
              label={t("matches.team1")}
            />
            {formData.team1 && <TeamPreview teamId={formData.team1} />}
          </div>

          <div>
            <CustomDropdown
              name="team2"
              value={formData.team2}
              teams={teams}
              label={t("matches.team2")}
            />
            {formData.team2 && <TeamPreview teamId={formData.team2} />}
          </div>

          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              {t("matches.matchDateTime")}
            </label>
            <input
              type="datetime-local"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
              required
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 px-4 rounded-md text-white font-medium ${
              loading
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            }`}
          >
            {loading ? t("common.creating") : t("matches.createMatch")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateMatch;
